import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";
import { getMovieById, getRelatedMovies } from "@/utils/movieData";
import { reviews, watchGuides } from "@/mocks/reviews";
import MovieCard from "@/components/feature/MovieCard";
import Breadcrumb from "@/components/feature/Breadcrumb";
import SectionTitle from "@/components/feature/SectionTitle";

const REVIEW_PAGE_SIZE = 4;

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const movie = getMovieById(id);
  const [reviewPage, setReviewPage] = useState(1);

  useDocumentMeta({
    title: movie ? `${movie.title} - 秋霞电影详细介绍与观影指南` : "影片详情 - 秋霞电影",
    description: movie
      ? `${movie.title}（${movie.year}，${movie.region}）由${movie.director}执导，${movie.synopsis.slice(0, 60)}...`
      : "秋霞电影影片详情页",
    keywords: movie
      ? `${movie.title},${movie.genres.join(",")},秋霞电影,电影介绍`
      : "秋霞电影,影片详情",
  });

  const related = useMemo(() => (movie ? getRelatedMovies(movie, 4) : []), [movie]);

  if (!movie) {
    return (
      <div className="mx-auto flex max-w-[1280px] flex-col items-center justify-center px-4 py-24 text-center md:px-6">
        <span className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 text-3xl">
          <i className="ri-emotion-sad-line"></i>
        </span>
        <h1 className="mt-5 font-heading text-2xl font-bold text-foreground-950">影片不存在或已下架</h1>
        <p className="mt-2 text-sm text-foreground-600">你访问的影片可能已被移除，返回首页看看其他好电影吧。</p>
        <Link
          to="/"
          className="mt-6 rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const totalReviewPages = Math.max(1, Math.ceil(reviews.length / REVIEW_PAGE_SIZE));
  const currentReviewPage = Math.min(reviewPage, totalReviewPages);
  const pageReviews = reviews.slice(
    (currentReviewPage - 1) * REVIEW_PAGE_SIZE,
    currentReviewPage * REVIEW_PAGE_SIZE
  );

  return (
    <div className="bg-background-50">
      {/* hero band */}
      <div className="relative border-b border-background-200 bg-primary-950">
        <div className="absolute inset-0 overflow-hidden">
          <img src={movie.backdrop} alt={`${movie.title} 背景`} className="h-full w-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-r from-primary-950 via-primary-950/85 to-primary-950/60"></div>
        </div>
        <div className="relative mx-auto max-w-[1280px] px-4 py-8 md:px-6 md:py-10">
          <div className="[&_a]:text-background-300 [&_a:hover]:text-accent-300 [&_span]:text-background-400">
            <Breadcrumb
              items={[
                { label: "首页", to: "/" },
                { label: "电影分类", to: `/category/${movie.genres[0] === "科幻" ? "sci-fi" : "popular"}` },
                { label: movie.title },
              ]}
            />
          </div>

          <div className="mt-6 flex flex-col gap-7 md:flex-row">
            <div className="w-[220px] shrink-0 self-center md:self-auto">
              <div className="aspect-[2/3] w-full overflow-hidden rounded-lg border border-primary-800">
                <img src={movie.poster} alt={`${movie.title} 海报`} title={`${movie.title} - 秋霞电影`} className="h-full w-full object-cover" />
              </div>
            </div>

            <div className="min-w-0 flex-1">
              <h1 className="font-heading text-2xl font-bold text-background-50 md:text-4xl">{movie.title}</h1>
              <p className="mt-3 text-sm text-accent-300">{movie.tagline}</p>

              <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-background-300">
                <span className="flex items-center gap-1.5">
                  <span className="w-4 h-4 flex items-center justify-center text-accent-400"><i className="ri-star-fill"></i></span>
                  <span className="font-semibold text-background-50">{movie.rating.toFixed(1)}</span> 分
                </span>
                <span>{movie.year}</span>
                <span>{movie.region}</span>
                <span>{movie.duration}</span>
                <span>{movie.releaseDate} 上映</span>
              </div>

              <div className="mt-5 flex flex-wrap gap-2">
                {movie.genres.map((g) => (
                  <span key={g} className="rounded-md border border-background-100/30 px-2.5 py-1 text-xs text-background-100">
                    {g}
                  </span>
                ))}
                {movie.tags.map((t) => (
                  <span key={t} className="rounded-md bg-accent-500 px-2.5 py-1 text-xs text-background-50">{t}</span>
                ))}
              </div>

              <dl className="mt-6 grid grid-cols-1 gap-y-2.5 text-sm sm:grid-cols-2">
                <div className="flex gap-2">
                  <dt className="text-background-400">导演</dt>
                  <dd className="text-background-100">{movie.director}</dd>
                </div>
                <div className="flex gap-2">
                  <dt className="text-background-400">主演</dt>
                  <dd className="text-background-100">{movie.actors.join("、")}</dd>
                </div>
              </dl>

              <div className="mt-7 flex flex-wrap gap-3">
                <Link
                  to="/category/popular"
                  className="inline-flex items-center gap-2 rounded-md bg-accent-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-accent-600 whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-play-circle-line"></i></span>
                  在线观看（演示）
                </Link>
                <button
                  type="button"
                  className="inline-flex items-center gap-2 rounded-md border border-background-100/40 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-background-50/10 cursor-pointer whitespace-nowrap"
                >
                  <span className="w-4 h-4 flex items-center justify-center"><i className="ri-heart-3-line"></i></span>
                  收藏
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-[1280px] px-4 py-10 md:px-6 md:py-12">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-3">
          <div className="lg:col-span-2">
            {/* synopsis */}
            <section>
              <SectionTitle title="影片简介" subtitle={`${movie.title} · ${movie.year}`} />
              <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
                <p className="text-sm leading-7 text-foreground-700 md:text-[15px]">{movie.synopsis}</p>
                <p className="mt-4 text-sm leading-7 text-foreground-700 md:text-[15px]">
                  本片由{movie.director}执导，{movie.actors.join("、")}联袂出演。
                  影片以{movie.genres.join("与")}元素为基调，在{movie.duration}的篇幅里完成了完整的情绪铺陈，
                  是秋霞电影编辑部推荐的{Number(movie.year) >= 2024 ? "年度新作" : "口碑佳作"}之一。
                </p>
              </div>
            </section>

            {/* watch guide */}
            <section className="mt-10">
              <SectionTitle title="观影指南" subtitle="观影前不妨先看看这些提示" />
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                {watchGuides.map((g) => (
                  <div key={g.id} className="rounded-lg border border-background-200 bg-background-100 p-4">
                    <h4 className="flex items-center gap-2 text-sm font-semibold text-foreground-950">
                      <span className="w-5 h-5 flex items-center justify-center text-accent-600"><i className="ri-lightbulb-line"></i></span>
                      {g.title}
                    </h4>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-600">{g.text}</p>
                  </div>
                ))}
              </div>
            </section>

            {/* reviews */}
            <section className="mt-10">
              <SectionTitle
                title="观众评论"
                subtitle={`共 ${reviews.length} 条评价（演示数据）`}
                eyebrow="影迷说"
                icon={<i className="ri-chat-quote-line"></i>}
              />
              <ul className="space-y-4">
                {pageReviews.map((r) => (
                  <li key={r.id} className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
                    <div className="flex items-start gap-3">
                      <span className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-100 text-primary-700 text-lg shrink-0">
                        <i className="ri-user-3-line"></i>
                      </span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                          <span className="text-sm font-medium text-foreground-950">{r.user}</span>
                          <span className="flex items-center gap-0.5 text-accent-500">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <span key={i} className="w-3.5 h-3.5 flex items-center justify-center">
                                <i className={i < Math.round(r.rating / 2) ? "ri-star-fill" : "ri-star-line"}></i>
                              </span>
                            ))}
                          </span>
                          <span className="text-xs text-foreground-500">{r.date}</span>
                        </div>
                        <p className="mt-2 text-sm leading-relaxed text-foreground-700">{r.content}</p>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              {totalReviewPages > 1 && (
                <div className="mt-6 flex items-center justify-center gap-2">
                  <button
                    type="button"
                    disabled={currentReviewPage === 1}
                    onClick={() => setReviewPage(currentReviewPage - 1)}
                    className="flex h-9 items-center gap-1 rounded-md border border-background-300 px-3 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                  >
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-left-s-line"></i></span>
                    上一页
                  </button>
                  <span className="text-sm text-foreground-600">{currentReviewPage} / {totalReviewPages}</span>
                  <button
                    type="button"
                    disabled={currentReviewPage === totalReviewPages}
                    onClick={() => setReviewPage(currentReviewPage + 1)}
                    className="flex h-9 items-center gap-1 rounded-md border border-background-300 px-3 text-sm text-foreground-700 transition-colors hover:border-primary-400 hover:text-primary-700 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                  >
                    下一页
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line"></i></span>
                  </button>
                </div>
              )}
            </section>
          </div>

          {/* sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-28 space-y-6">
              <div className="rounded-lg border border-background-200 bg-background-100 p-5">
                <h3 className="text-sm font-semibold text-foreground-950">影片信息</h3>
                <ul className="mt-3 space-y-2.5 text-sm">
                  <li className="flex justify-between gap-3"><span className="text-foreground-500">导演</span><span className="text-foreground-800 text-right">{movie.director}</span></li>
                  <li className="flex justify-between gap-3"><span className="text-foreground-500">类型</span><span className="text-foreground-800 text-right">{movie.genres.join(" / ")}</span></li>
                  <li className="flex justify-between gap-3"><span className="text-foreground-500">地区</span><span className="text-foreground-800 text-right">{movie.region}</span></li>
                  <li className="flex justify-between gap-3"><span className="text-foreground-500">上映</span><span className="text-foreground-800 text-right">{movie.releaseDate}</span></li>
                  <li className="flex justify-between gap-3"><span className="text-foreground-500">片长</span><span className="text-foreground-800 text-right">{movie.duration}</span></li>
                  <li className="flex justify-between gap-3"><span className="text-foreground-500">评分</span><span className="text-accent-700 font-semibold text-right">{movie.rating.toFixed(1)}</span></li>
                </ul>
              </div>

              <div className="rounded-lg border border-background-200 bg-background-50 p-5">
                <h3 className="text-sm font-semibold text-foreground-950">快捷入口</h3>
                <div className="mt-3 space-y-2">
                  <Link to="/" className="flex items-center justify-between rounded-md border border-background-200 px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700">
                    返回首页
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line"></i></span>
                  </Link>
                  <Link to="/category/popular" className="flex items-center justify-between rounded-md border border-background-200 px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700">
                    返回热门分类
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line"></i></span>
                  </Link>
                  <Link to="/news" className="flex items-center justify-between rounded-md border border-background-200 px-3 py-2.5 text-sm text-foreground-700 transition-colors hover:border-primary-300 hover:text-primary-700">
                    查看影视资讯
                    <span className="w-4 h-4 flex items-center justify-center"><i className="ri-arrow-right-s-line"></i></span>
                  </Link>
                </div>
              </div>
            </div>
          </aside>
        </div>

        {/* related */}
        {related.length > 0 && (
          <section className="mt-12">
            <SectionTitle
              title="相关影片推荐"
              subtitle="同类型或同导演的更多作品"
              eyebrow="猜你喜欢"
              icon={<i className="ri-magic-line"></i>}
            />
            <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
              {related.map((m) => (
                <MovieCard key={m.id} movie={m} />
              ))}
            </div>
          </section>
        )}
      </div>
    </div>
  );
}